import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import posService from "../../services/pos/pos.service";

interface OtherPosStock {
  posId: number;
  posCode: string;
  productId: number;
  productName: string;
  totalStock: number;
  totalAvailableStock: number;
}

interface RequestDialogProps {
  visible: boolean;
  productId: number | null;
  posId: number | null;
  productName: string;
  posName: string;
  onHide: () => void;
  onSuccess: () => void;
  minOrderQty?: number;
}

const RequestDialog: React.FC<RequestDialogProps> = ({
  visible,
  productId,
  posId,
  productName,
  posName,
  onHide,
  onSuccess,
  minOrderQty,
}) => {
  const toast = useRef<Toast>(null);
  const submitting = useRef(false);
  const [otherPosList, setOtherPosList] = useState<OtherPosStock[]>([]);
  const [selectedPosStock, setSelectedPosStock] = useState<OtherPosStock | null>(null);
  const [quantity, setQuantity] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const allowDecimals = minOrderQty ? minOrderQty % 1 !== 0 : false;

  const showMessage = (summary: string, detail: string, severity: any) => {
    toast.current?.show({
      severity,
      summary,
      detail,
      life: 3000,
    });
  };

  const fetchOtherPosStock = async () => {
    if (!productId || !posId) return;

    try {
      setLoading(true);
      const response = await posService.getOtherPosStock(posId, productId);

      if (response && Array.isArray(response)) {
        // Availability is informational: a request may be fulfilled after restocking.
        setOtherPosList(response);
      } else {
        setOtherPosList([]);
      }
    } catch (error) {
      console.error("Error fetching other POS stock:", error);
      showMessage("Error", "Failed to load other POS stock", "error");
      setOtherPosList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchOtherPosStock();
      // Reset form
      setSelectedPosStock(null);
      setQuantity(null);
    }
  }, [visible, productId, posId]);

  const handleSubmitRequest = async () => {
    if (loading || submitting.current) return;
    // Validation
    if (!selectedPosStock) {
      showMessage("Validation Error", "Please select a POS", "warn");
      return;
    }

    if (!quantity || !Number.isFinite(quantity) || quantity <= 0) {
      showMessage("Validation Error", "Quantity must be greater than 0", "warn");
      return;
    }

    submitting.current = true;
    try {
      setLoading(true);
      const payload = {
        productId: productId,
        requestFromPOS: posId,
        requestToPOS: selectedPosStock.posId,
        quantity: quantity!,
      };

      const response = await posService.AddPosrequest(payload);

      if (response?.isSuccess || response?.success) {
        showMessage(
          "Success",
          response.message || "Stock request submitted successfully",
          "success"
        );
        onSuccess();
        onHide();
      } else {
        showMessage("Error", response?.message || "Failed to submit request", "error");
      }
    } catch (error) {
      console.error("Error submitting request:", error);
      showMessage("Error", "An error occurred while submitting request", "error");
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  const posOptionTemplate = (option: OtherPosStock) => {
    return (
      <div className="flex align-items-center justify-content-between gap-2">
        <span className="font-semibold">{option.posCode}</span>
        <span className="text-sm text-gray-600">
          {option.totalAvailableStock} available
        </span>
      </div>
    );
  };

  const selectedPosTemplate = (option: OtherPosStock | null) => {
    if (!option) return <span>Select a POS</span>;
    return (
      <div className="flex align-items-center justify-content-between gap-2">
        <span className="font-semibold">{option.posCode}</span>
        <span className="text-sm text-gray-600">
          {option.totalAvailableStock} available
        </span>
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={visible}
        onHide={onHide}
        header="Request Stock"
        modal
        className="p-fluid"
        style={{ width: "40vw" }}
        breakpoints={{ "960px": "75vw", "641px": "95vw" }}
      >
        <div>
          {/* POS Name and Product Name */}
          <div className="field mb-3">
            <label className="font-semibold text-sm block mb-1">Product & POS</label>

            <div className="flex gap-2">
              {/* POS (30%) */}
              <div style={{ flex: "0 0 30%" }}>
                <InputText
                  value={posName}
                  disabled
                  className="w-full"
                  style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
                />
              </div>

              {/* Product Name (70%) */}
              <div style={{ flex: "1 0 70%" }}>
                <InputText
                  value={productName}
                  disabled
                  className="w-full"
                  style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
                />
              </div>
            </div>
          </div>

          {/* Quantity */}
          <div className="field mb-3">
            <label htmlFor="quantity" className="font-semibold text-sm block mb-1">
              Requested Quantity <span className="text-red-500">*</span>
            </label>
            <InputNumber
              inputId="quantity"
              value={quantity}
              onValueChange={(e) => setQuantity(e.value ?? null)}
              placeholder="Enter quantity to request"
              min={0}
              disabled={loading}
              minFractionDigits={0} 
              maxFractionDigits={allowDecimals ? 3 : 0} 
              useGrouping={false}
              className="w-full"
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
            {selectedPosStock && (
              <small className="text-gray-600 block mt-1">
                Currently available: {selectedPosStock.totalAvailableStock}. You can request more than this.
              </small>
            )}
          </div>

          {/* POS Selection */}
          <div className="field mb-3">
            <label htmlFor="targetPos" className="font-semibold text-sm block mb-1">
              Request From POS <span className="text-red-500">*</span>
            </label>
            <Dropdown
              inputId="targetPos"
              value={selectedPosStock}
              options={otherPosList}
              onChange={(e) => setSelectedPosStock(e.value)}
              optionLabel="posCode"
              placeholder="Select a POS to request from"
              filter
              filterBy="posCode"
              itemTemplate={posOptionTemplate}
              valueTemplate={selectedPosTemplate}
              className="w-full"
              disabled={loading || otherPosList.length === 0}
              emptyMessage="No other POS found for this product"
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-2 justify-content-end mt-4">
            <Button
              label="Cancel"
              onClick={onHide}
              severity="secondary"
              className="text-black border-round p-button-m"
              disabled={loading}
            />
            <Button
              label="Submit Request"
              onClick={handleSubmitRequest}
              loading={loading}
              // disabled={!selectedPosStock || quantity <= 0}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default RequestDialog;
